import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Search, MapPin, Star, Phone, Mail, Plus, ShieldCheck, Soup, Shirt, Wifi, Sparkles, Wrench, Lock, Briefcase, Building2 } from "lucide-react";

const CATEGORIES = [
  { v: "food", label: "Food / Tiffin", icon: Soup, color: "lime" },
  { v: "laundry", label: "Laundry", icon: Shirt, color: "pink" },
  { v: "wifi", label: "Wifi / Internet", icon: Wifi, color: "blue" },
  { v: "cleaning", label: "Cleaning", icon: Sparkles, color: "lime" },
  { v: "security", label: "Security", icon: Lock, color: "white" },
  { v: "maintenance", label: "Maintenance", icon: Wrench, color: "pink" },
  { v: "tiffin", label: "Tiffin Service", icon: Soup, color: "lime" },
  { v: "stationery", label: "Stationery", icon: Briefcase, color: "blue" },
  { v: "other", label: "Other", icon: Briefcase, color: "white" },
];

export default function Vendors() {
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();
  const [cat, setCat] = useState(params.get("category") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/vendors", { params: { category: cat || undefined, city: city || undefined, q: q || undefined } });
      setItems(data);
    } catch { /* empty */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, city]);

  return (
    <div className="paper min-h-[80vh]" data-testid="vendors-page">
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-12 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="brut-tag pink">Hostro Marketplace</span>
            <h1 className="font-display font-black text-4xl md:text-5xl mt-3">Vendors for your PG.</h1>
            <p className="text-[#484848] mt-2 max-w-2xl">Verified tiffin, laundry, wifi, cleaning & maintenance partners that PG owners can plug in with a single click.</p>
          </div>
          <Link to="/vendors/register" className="brut-btn" data-testid="vendor-register-cta"><Plus size={16}/> List your service</Link>
        </div>

        <form onSubmit={(e)=>{e.preventDefault(); load();}} className="mt-6 brut-card p-2 flex flex-col sm:flex-row gap-2" data-testid="vendor-search">
          <div className="flex-1 flex items-center gap-2 px-3"><Search size={18}/><input className="w-full bg-transparent py-3 outline-none font-medium" placeholder="Search by name or service…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="vendor-q"/></div>
          <div className="flex items-center gap-2 px-3 sm:border-l-2 border-[#0F1F17]"><MapPin size={18}/><input className="w-full sm:w-44 bg-transparent py-3 outline-none font-medium" placeholder="City" value={city} onChange={(e)=>setCity(e.target.value)} data-testid="vendor-city"/></div>
          <button className="brut-btn" data-testid="vendor-search-btn">Search</button>
        </form>

        <div className="flex flex-wrap gap-2 mt-5">
          <button onClick={()=>setCat("")} className={`brut-tag ${!cat ? "lime" : "white"} cursor-pointer`} data-testid="vendor-cat-all">All ({items.length})</button>
          {CATEGORIES.map(c => (
            <button key={c.v} onClick={()=>setCat(c.v === cat ? "" : c.v)} className={`brut-tag ${cat === c.v ? c.color : "white"} cursor-pointer flex items-center gap-1`} data-testid={`vendor-cat-${c.v}`}>
              <c.icon size={12}/> {c.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20">
        {items.length === 0 ? (
          <div className="brut-card p-12 text-center">
            <Briefcase size={32} className="mx-auto"/>
            <div className="font-display font-black text-2xl mt-2">No vendors yet in this category.</div>
            <p className="text-[#484848] mt-1">Are you a vendor? Get listed for free.</p>
            <Link to="/vendors/register" className="brut-btn mt-4 inline-flex">List your service</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendor-grid">
            {items.map(v => <VendorCard key={v.id} v={v} canSubscribe={user?.role === "owner"}/>)}
          </div>
        )}
      </section>
    </div>
  );
}

function VendorCard({ v, canSubscribe }) {
  const cat = CATEGORIES.find(c => c.v === v.category) || CATEGORIES[CATEGORIES.length - 1];
  const Icon = cat.icon;
  return (
    <div className="brut-card overflow-hidden bg-white" data-testid={`vendor-${v.id}`}>
      {v.images?.[0] ? (
        <div className="h-40 brut-border-b overflow-hidden"><img src={v.images[0]} alt="" className="w-full h-full object-cover"/></div>
      ) : (
        <div className={`h-40 flex items-center justify-center bg-[${cat.color === "lime" ? "#ECFAF1" : "#FFEAF6"}]`}><Icon size={40}/></div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-display font-black text-xl">{v.business_name}</div>
            <div className="text-xs text-[#484848] mt-1">{v.name} · {v.city}</div>
          </div>
          <span className={`brut-tag ${cat.color} !text-[10px] flex items-center gap-1`}><Icon size={10}/> {v.category}</span>
        </div>
        <p className="text-sm text-[#484848] mt-3 line-clamp-2">{v.description || "No description provided."}</p>
        <div className="flex items-center justify-between mt-4 gap-2 text-sm">
          <div className="flex items-center gap-1"><Star size={14} className="fill-[#FFC857]"/> <strong>{v.rating || 0}</strong> <span className="text-xs text-[#484848]">({v.review_count || 0})</span></div>
          <Link to={`/vendors/${v.id}`} className="brut-btn white !py-1 !px-3 !text-xs" data-testid={`vendor-view-${v.id}`}>View</Link>
        </div>
        {["food", "tiffin"].includes(v.category) && <Link to={`/food/${v.id}`} className="brut-btn !py-1.5 !px-3 !text-xs w-full mt-2 justify-center" data-testid={`order-food-${v.id}`}>Order food →</Link>}
        {canSubscribe && <div className="mt-2 text-xs text-[#484848]">Owner? Subscribe from your dashboard → Vendors tab.</div>}
      </div>
    </div>
  );
}
